<template>
  <FormHeader
    :formStep="formStep"
    :backBtnText="t('button.cancel')"
    :showBackBtn="!isIv24FunnelActive"
    @onBackClick="handleBackButtonClick"
  />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle>{{ t('propertyType.title') }}</FormTitle>

      <FormContent>
        <div :class="$style.propertyRadioBoxWrapper">
          <PropertyRadioBox
            :text="t('common.house')"
            value="1"
            :checked="checkedPropertyType"
            @onClick="onItemClick"
          />
          <PropertyRadioBox
            :text="t('common.apartment')"
            value="0"
            :checked="checkedPropertyType"
            @onClick="onItemClick"
          />
        </div>
      </FormContent>
    </TransitionWrapper>
    <ValuationFunnelErrorText v-if="isShowErrorMessage" />
  </FormContainer>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import {
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  TransitionWrapper,
} from '@/components/common';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { useCommonStore } from '@/stores/common/common';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { hasHowProperties } from '@/tracking/howPropertiesTracking';
import { PageSource, ValuationFlow } from '@/types';

import ValuationFunnelErrorText from '@/components/common/ValuationFunnelErrorText/ValuationFunnelErrorText.vue';
import PropertyRadioBox from '@/components/property-valuation-page/PropertyType/components/PropertyRadioBox';

const commonStore = useCommonStore();
const propertyValuationStore = usePropertyValuationStore();
const { data: formData, step: formStep, isIv24FunnelActive } = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

// Reactive data
const isShowErrorMessage = ref(false);
const checkedPropertyType = computed(() =>
  formData.value.propertyType === undefined ? '' : formData.value.propertyType.toString(),
);

// Methods
const handleBackButtonClick = (): void => {
  const origin = window.location.origin;
  if (route.query?.source === PageSource.MarketAtlas) {
    window.location.href = `${origin}/${locale.value}/${t('marketAtlasUrl')}`;
    return;
  }
  router.back();
};

const onItemClick = (value: string): void => {
  isShowErrorMessage.value = false;
  propertyValuationStore.setFormData({
    propertyType: parseInt(value),
  });
  onNext();
};

const onNext = (): void => {
  if (formData.value.propertyType === undefined) {
    isShowErrorMessage.value = true;
    return;
  }
  propertyValuationStore.navigateNext(ValuationFlow.PropertyType);
};

onMounted(async () => {
  const baseParams = {
    subType: 'input_category',
    locale: locale.value,
    stepInputs:
      route?.query?.source === PageSource.MarketAtlas ? `source=${PageSource.MarketAtlas}` : '',
  };

  const isUserLoggedIn = commonStore.loggedIn;
  const userHasProperties = isUserLoggedIn ? await hasHowProperties() : undefined;

  trackVirtualPageViewWithTreatment({
    ...baseParams,
    ...(isUserLoggedIn && {
      moreParams: {
        h_user_group_039: userHasProperties ? 'is_how_user' : 'not_how_user',
      },
    }),
  });
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.propertyRadioBoxWrapper {
  display: flex;
  gap: $space-150;
  justify-content: space-between;

  > div {
    width: 50%;
  }

  @include grid-media-query(sm) {
    gap: $space-150;
  }
}
</style>
